import * as a from '@mitranim/js/all.mjs'
import * as c from './conf.mjs'
import * as dmi from './dat-misc.mjs'
import * as u from './util.mjs'
import {E} from './util.mjs'

export function jsonLdMeta(val) {
  if (!a.isStruct(val)) return undefined

  return E(`script`, {type: `application/ld+json`},
    u.escapeScriptContent(a.jsonEncode(val))
  )
}

/**
 * spec: http://schema.org/Organization
 * validator: https://search.google.com/structured-data/testing-tool
 * Generates Organization's Schema in JSON-LD format
 */
export function jsonLdWebSiteSchema() {
  return {
    "@context": schemaUrl(),
    "@type": `WebSite`,
    "url": c.SERVER_ORIGIN,

    // https://developers.google.com/search/docs/appearance/structured-data/sitelinks-searchbox
    "potentialAction": {
      "@type": `SearchAction`,
      "target": u.googleSearchTemplateUrl(),
      "query-input": `required name=search_term_string`
    },

    // TODO recover.
    //
    // 'logo': {
    //   '@type': 'ImageObject',
    //   'url': u.siteUrl('/images/logo.png'),
    // },
  }
}

/**
 * spec: http://schema.org/Article
 * validator: https://search.google.com/structured-data/testing-tool
 * Generates Article's Schema in JSON-LD format
 * @param {article}
 * @returns {object}
 */
export function jsonLdArticleSchema(article) {
  if (!a.optStruct(article)) return undefined

  return {
    "@context": schemaUrl(),
    "@type": `Article`,
    "mainEntityOfPage": {
      "@type": `WebPage`,
      "@id": `https://google.com/article`,
    },
    "headline": article.title,
    "image": u.imageUrl(article.image),
    "author": {
      "@type": `Person`,
      "name": dmi.guessPersonName(article.person),
    },
    "publisher": {
      "@type": `Organization`,
      "name": c.BRAND_NAME,
      "logo": {
        "@type": `ImageObject`,
        "url": c.BRAND_LOGO_FULL_PATH,
      },
    },
    "datePublished": !article.createdAt
      ? undefined
      : u.formatDateHumanWithWeekday(article.createdAt),
    "dateModified": !article.updatedAt && !article.createdAt
      ? undefined
      : u.formatDateHumanWithWeekday(article.updatedAt || article.createdAt),
    "articleBody": article.content,
    "description": article.preview,
  }
}

/*
TODO verify where this should be used. This looks like something that should be
used for article feed, not for individual article.
*/
export function jsonLdArticleBreadcrumbSchema(article) {
  if (!a.optStruct(article)) return undefined

  return {
    "@context": schemaUrl(),
    "@type": `BreadcrumbList`,
    "itemListElement": [{
      "@type": `ListItem`,
      "position": 1,
      "item": {
        "@id": c.SERVER_ORIGIN,
        "name": `Home`,
      },
    }, {
      "@type": `ListItem`,
      "position": 2,
      "item": {
        "@id": u.siteUrl(c.LINK_ARTICLES),
        "name": `Articles`,
      },
    },
    {
      "@type": `ListItem`,
      "position": 3,
      "item": {
        "@id": dmi.articleEntLink(article) || u.siteUrl(c.LINK_ARTICLES),
        "name": article.title,
      },
    }],
  }
}

function serviceFeedPageUrl({ent, type}) {
  const {nature} = a.laxStruct(ent)

  if (nature === c.SERVICE_NATURE_SESSION || type === c.VIRT_TYPE_SERVICE) return u.siteUrl(c.LINK_SERVICES)
  if (nature === c.SERVICE_NATURE_EVENT || type === c.VIRT_TYPE_EVENT) return u.siteUrl(c.LINK_EVENTS)
  if (nature === c.SERVICE_NATURE_VIDEO || type === c.VIRT_TYPE_VIDEO) return u.siteUrl(c.LINK_VIDEOS)

  return undefined
}

function serviceFeedPageTitle({ent, type}) {
  const {nature} = a.laxStruct(ent)

  if (nature === c.SERVICE_NATURE_SESSION || type === c.VIRT_TYPE_SERVICE) return `Services`
  if (nature === c.SERVICE_NATURE_EVENT || type === c.VIRT_TYPE_EVENT) return `Events`
  if (nature === c.SERVICE_NATURE_VIDEO || type === c.VIRT_TYPE_VIDEO) return `Videos`

  return undefined
}

// TODO articles, persons, etc.
export function jsonLdServiceFeedBreadcrumbSchema({ent, type, isSinglePage}) {
  if (!(a.optStruct(ent) || a.vac(type))) return undefined

  return {
    "@context": schemaUrl(),
    "@type": `BreadcrumbList`,
    "itemListElement": [
      {
        "@type": `ListItem`,
        "position": 1,
        "item": {
          "@id": c.SERVER_ORIGIN,
          "name": `Home`, // TODO revise.
        },
      },
      {
        "@type": `ListItem`,
        "position": 2,
        "item": {
          "@id": serviceFeedPageUrl({ent, type}),
          "name": serviceFeedPageTitle({ent, type}),
        },
      },
      a.vac(isSinglePage) && {
        "@type": `ListItem`,
        "position": 3,
        "item": {
          "@id": u.siteUrl(dmi.serviceEntLink(ent)) || serviceFeedPageUrl({ent, type}),
          "name": ent.title,
        },
      },
    ]
  }
}

export function jsonLdProductSchema({ent}) {
  if (!a.optStruct(ent)) return undefined
  if (!ent.rating && !ent.priceCents) return undefined

  return {
    "@context": schemaUrl(),
    "@type": `Product`,
    "image": u.imageUrl(ent.image),
    "name": ent.title,
    "description": ent.preview,
    "brand": {
      "@type": `Brand`,
      "name": dmi.guessPersonName(ent.person),
    },
    ...a.vac(ent.priceCents) && {
      "offers": {
        "@type": `Offer`,
        "url": u.siteUrl(dmi.serviceEntLink(ent)) || serviceFeedPageUrl({ent}),
        "price": u.formatNumber(u.moneyCentsToUnits(ent.priceCents), 2),
        "priceCurrency": a.upper(ent.priceCurrency),
        "priceValidUntil": undefined,
        "itemCondition": schemaUrl(`UsedCondition`),
        "availability": schemaUrl(`InStock`),
      },
    },
    ...a.vac(ent.rating) && {
      "aggregateRating": {
        "@type": `AggregateRating`,
        "ratingValue": u.formatNumber(ent.rating, 2),
        "bestRating": `5`,
        "ratingCount": ent.reviewCount
      },
    },
  }
}

function schemaUrl(pathname) {
  const url = u.url(c.SCHEMA_ORG_URL)
  if (a.isStr(pathname)) url.addPath(pathname)
  return url.toString()
}
